// Scrolls to en element or position in the page, allows animation.
//
// @param selector [number|HTMLElement|string] - position (scrollTop), HTMLElement or selector to scroll to
// @param options [Object]
//   .offset [number] (0) - offsets the target position
//   .behavior [ScrollBehavior] ("smooth") - scroll behavior
import isString from "lodash/isString";

type ScrollOptions = {
  offset?: number;
  behavior?: ScrollBehavior;
};

type ElementSelector = HTMLElement | string;

const defaultOptions: Required<ScrollOptions> = { offset: 0, behavior: "smooth" };

const getElement = (selector: ElementSelector): HTMLElement | null => {
  if (isString(selector)) {
    return document.querySelector(selector);
  }
  if (selector instanceof HTMLElement) {
    return selector;
  }
  return null;
};

const getScrollTop = (selector: ElementSelector | number): number | null => {
  if (typeof selector === "number") {
    return selector;
  }

  const element = getElement(selector);
  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  return rect.top + window.pageYOffset;
};

export const scroll = (
  selector: ElementSelector | number,
  optionsProvided: ScrollOptions = {}
): NodeJS.Timeout | undefined => {
  const options: Required<ScrollOptions> = { ...defaultOptions, ...optionsProvided };

  const scrollTop = getScrollTop(selector);

  // Early return if the element is not found
  if (scrollTop === null) {
    return;
  }

  const targetPosition = scrollTop + options.offset;

  return setTimeout(() => {
    window.scrollTo({
      top: targetPosition,
      behavior: options.behavior
    });
  }, 0);
};
